import { For } from "solid-js";
import { Dynamic } from "solid-js/web";
import { COMPETITORS, STATUS, type Status, TABLE_GROUPS } from "~/components/landing/compareData";
import { SectionTitle, TextLink } from "~/components/marketing/ui";
import { useI18n } from "~/i18n";
import type { TranslationKey } from "~/i18n/translations";

// Every cell comes from the /compare page's sourced table; the homepage only picks rows and words their labels.
const ROWS: { id: string; label: TranslationKey }[] = [
  { id: "bot_free", label: "marketing.home.compare.row_bot_free" },
  { id: "live_transcript", label: "marketing.home.compare.row_live_transcript" },
  { id: "live_notes", label: "marketing.home.compare.row_live_notes" },
  { id: "updates_tools", label: "marketing.home.compare.row_updates_tools" },
  { id: "agent_handoff", label: "marketing.home.compare.row_agent_handoff" },
  { id: "byok", label: "marketing.home.compare.row_byok" },
  { id: "write_mcp", label: "marketing.home.compare.row_write_mcp" },
];

const STATUS_LABEL: Record<Status, TranslationKey> = {
  yes: "marketing.home.compare.status_yes",
  partly: "marketing.home.compare.status_partly",
  no: "marketing.home.compare.status_no",
  unknown: "marketing.home.compare.status_unknown",
};

const VENDOR_KEYS = ["granola", "otter", "fathom"] as const;

const Cell = (props: { status: Status }) => {
  const { t } = useI18n();
  return (
    <>
      <Dynamic
        component={STATUS[props.status].icon}
        class={`mx-auto size-5 ${STATUS[props.status].class}`}
        aria-hidden="true"
      />
      <span class="sr-only">{t(STATUS_LABEL[props.status])}</span>
    </>
  );
};

const row = (id: string) => TABLE_GROUPS.flatMap((group) => group.rows).find((candidate) => candidate.id === id);

export const CompareSection = () => {
  const { t } = useI18n();

  return (
    <section aria-labelledby="home-compare" class="px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
      <div class="mx-auto max-w-4xl">
        <SectionTitle id="home-compare" text={t("marketing.home.compare.title")} linked class="text-center" />
        <div class="mt-12 overflow-hidden rounded-2xl border border-primary-border bg-tile">
          <table class="w-full table-fixed border-collapse text-sm sm:text-base">
            <thead>
              <tr class="border-b border-primary-border bg-panel">
                <th scope="col" class="w-[46%] p-4 text-start sm:px-6">
                  <span class="sr-only">{t("marketing.home.compare.capability")}</span>
                </th>
                <th scope="col" class="px-2 py-4">
                  <img src="/images/chief-logo-gold-crown-only.svg" alt="Chief" class="mx-auto h-5 w-auto" />
                </th>
                <For each={COMPETITORS}>
                  {(vendor) => (
                    <th scope="col" class="px-2 py-4">
                      <span class={`mx-auto grid size-7 place-items-center overflow-hidden rounded-full ${vendor.bg}`}>
                        <img
                          src={vendor.src}
                          alt={vendor.name}
                          class={vendor.bg ? "size-4 object-contain brightness-0 invert" : "size-full object-cover"}
                        />
                      </span>
                    </th>
                  )}
                </For>
              </tr>
            </thead>
            <tbody>
              <For each={ROWS}>
                {(item) => (
                  <tr class="border-b border-primary-border last:border-b-0">
                    <th scope="row" class="p-4 text-start font-medium sm:px-6">
                      {t(item.label)}
                    </th>
                    <td class="bg-brand/5 px-2 py-4 text-center">
                      <Cell status={row(item.id)?.chief.status ?? "unknown"} />
                    </td>
                    <For each={VENDOR_KEYS}>
                      {(vendor) => (
                        <td class="px-2 py-4 text-center">
                          <Cell status={row(item.id)?.[vendor].status ?? "unknown"} />
                        </td>
                      )}
                    </For>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
        <div class="mt-6 flex flex-col items-center gap-3 text-center">
          <p class="text-sm text-secondary-text">{t("marketing.home.compare.footnote")}</p>
          <TextLink href="/compare" label={t("marketing.home.compare.link")} />
        </div>
      </div>
    </section>
  );
};
